import {
  map,
  flatMap,
  mapValues,
  reduce,
  filter,
  chunk,
  sortBy,
  difference,
  take,
  get,
  omitBy,
  pick,
  pickBy,
  groupBy,
  curry,
  isNil,
  isNaN,
  zipObject,
  intersection,
  without,
  find,
  uniq,
  countBy,
  mapKeys,
  camelCase,
  snakeCase,
  reject,
  flowRight,
  split,
} from 'lodash';

// Mappings
export const compose = flowRight;

// map :: Function -> Collection -> Collection
export const curriedMap = fn => coll => map(coll, fn);

// flatMap :: Function -> Collection -> Collection
export const curriedFlatMap = fn => coll => flatMap(coll, fn);

// mapValues :: Function -> obj -> obj
export const curriedMapValues = fn => obj => mapValues(obj, fn);

// reduce :: (Function, Any) -> Collection -> Any
export const curriedReduce = (fn, initialValue) => coll => reduce(coll, fn, initialValue);

// filter :: Function -> Collection -> Collection
export const curriedFilter = fn => coll => filter(coll, fn);

// splitEvery :: Number -> Collection -> Collection
export const splitEvery = size => coll => chunk(coll, size);

// sortBy :: Function -> Collection -> Collection
export const curriedSortBy = fn => coll => sortBy(coll, fn);

// difference :: Collection -> Collection -> Collection
export const curriedDifference = listB => listA => difference(listA, listB);

// take :: Number -> Collection -> Collection
export const curriedTake = num => coll => take(coll, num);

// prop :: String -> Object -> Any|Undefined
export const prop = path => obj => get(obj, path);

// propOr :: (Any, String) -> Object -> Any
export const propOr = (fallback, path) => obj => get(obj, path, fallback);

// propEq :: (String, String) -> Object -> Boolean
export const propEq = (key, val) => obj => get(obj, key) === val;

// omitBy :: Function -> Object -> Object
export const curriedOmitBy = fn => obj => omitBy(obj, fn);

// pick :: Array -> Object -> Object
export const curriedPick = list => obj => pick(obj, list);

// pickBy :: Function -> Object -> Object
export const curriedPickBy = fn => obj => pickBy(obj, fn);

// groupBy :: Function -> Collection -> Object
export const curriedGroupBy = fn => coll => groupBy(coll, fn);

// test :: RegExp -> String -> Boolean
export const curriedTest = pattern => str => pattern.test(str);

// replaceAt :: Number -> Any -> Collection
export const replaceAt = curry((index, replacement, list) =>
  index >= 0 ? [...list.slice(0, index), replacement, ...list.slice(index + 1)] : list);

// equals :: Any -> Any -> Boolean
export const equals = x => y => x === y;

// always :: Any -> (* -> Any)
export const always = x => () => x;

// when :: (Function, Function) -> Any -> Any
export const when = (predicate, whenTrueFn) => val => (predicate(val) ? whenTrueFn(val) : val);

// defaultTo :: Any -> Any -> Any
export const curriedDefaultTo = def => val => (isNil(val) || isNaN(val) ? def : val);

// join :: String -> Collection -> String
export const curriedJoin = delimiter => coll => coll.join(delimiter);

// promisifyObject :: {Promise<Any>} -> Promise<{Any}>
export const promisifyObject = obj => Promise.all(Object.values(obj)).then(arr => zipObject(Object.keys(obj), arr));

// intersection :: Array -> Array -> Array
export const curriedIntersection = a => b => intersection(a, b);

// without :: Array -> Array -> Array
export const curriedWithout = toRemove => list => without(list, ...toRemove);

// find :: Function -> [Object] -> Object|Undefined
export const curriedFind = fn => coll => find(coll, fn);

// split :: String|Regexp -> String -> Array
export const curriedSplit = delimiter => string => split(string, delimiter);

// startsWith :: String -> String -> Boolean
// startsWith :: [Any] -> [Any] -> Boolean
export const curriedStartsWith = target => string => string.startsWith(target);

// length :: Array -> Number
export const curriedLength = arr => arr.length;

// uniq :: Array -> Array
export const curriedUniq = arr => uniq(arr);

// countBy :: Function -> Object -> Object
export const curriedCountBy = fn => obj => countBy(obj, fn);

// camelCase :: Object -> Object
export const keysToCamelCase = obj => mapKeys(obj, (_value, key) => camelCase(key));

// snakeCase :: Object -> Object
export const keysToSnakeCase = obj => mapKeys(obj, (_value, key) => snakeCase(key));

// includes :: String -> Collection -> Boolean
export const curriedIncludes = value => coll => coll.includes(value);

// reject :: Function -> Collection -> Collection
export const curriedReject = fn => coll => reject(coll, fn);
